import { z } from 'zod';

import { csvIdsSchema, dateOnlySchema, paginationQuerySchema, type Paginated } from './common.js';

/**
 * Consulta da trilha.
 *
 * O período é por dia inteiro: `to` inclui o dia informado até 23:59, senão
 * "de 1º a 31" perderia tudo o que aconteceu no dia 31 — o erro clássico de
 * comparar um instante contra uma data.
 */
export const auditQuerySchema = paginationQuerySchema.extend({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  userIds: csvIdsSchema,
  /** Verbos canônicos, como gravados: `obra.updated`, `auth.login`… */
  actions: z
    .string()
    .transform((value) =>
      value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== ''),
    )
    .pipe(z.array(z.string().max(60)).max(40))
    .optional(),
  entityType: z.string().trim().max(40).optional(),
  /** Alcança o resumo, o endereço de IP e o identificador da entidade. */
  search: z.string().trim().max(120).optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

export interface AuditPerson {
  readonly id: string;
  readonly name: string;
  readonly avatarUrl: string | null;
}

/** Um campo que mudou, do jeito que a tela mostra: antes → depois. */
export interface AuditChange {
  readonly field: string;
  readonly from: string;
  readonly to: string;
}

export interface AuditEntry {
  readonly id: string;
  readonly action: string;
  readonly summary: string;
  readonly entityType: string | null;
  readonly entityId: string | null;
  readonly ipAddress: string | null;
  readonly userAgent: string | null;
  readonly createdAt: string;
  /** Nulo quando quem agiu já não existe: a trilha não morre com o autor. */
  readonly user: AuditPerson | null;
  /** Extraído de `context.changes` quando a ação registrou um diff. */
  readonly changes: readonly AuditChange[];
  /** O contexto cru, para o que o diff não cobre. Nulo quando não há. */
  readonly context: string | null;
}

/** Uma ação disponível no filtro, com quantas vezes ocorreu no acervo. */
export interface AuditActionOption {
  readonly action: string;
  readonly total: number;
}

export interface AuditOptions {
  readonly actions: readonly AuditActionOption[];
  readonly entityTypes: readonly string[];
  readonly people: readonly AuditPerson[];
}

export type AuditResult = Paginated<AuditEntry>;
